import React, { useEffect, useState } from "react";
import {
  Users, TrendingUp, Lock, Activity, RefreshCw, ArrowRight, EyeOff,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  fetchAdminEngagement,
  fetchAdminStealthCohort,
  fetchAdminStealthUsers,
} from "../lib/analytics";
import AdminShell from "../components/AdminShell";

/**
 * AdminEngagement — /admin/engagement
 *
 * Investor-grade traction view. Six panels:
 *   1. Active users  · DAU / WAU / MAU
 *   2. Composition   · free vs Pro split, founding members
 *   3. Free user engagement · routine-count buckets, avg routines / active free
 *   4. Conversion funnel · signup → first routine → paywall → Pro
 *   5. Retention cohorts · weekly heatmap (last 8 weeks)
 *   6. Stealth mode  · % routines stealth, % users default-on
 *
 * Privacy: every panel is AGGREGATE-ONLY. No user_id, name, or email
 * ever appears in this view — stealth users contribute to counts but
 * are not individually listed.
 */

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString() : (n ?? "—");
const fmtPct = (n) =>
  typeof n === "number" ? `${n.toFixed(1)}%` : "—";

const StatTile = ({ label, value, hint, accent = "default", testid }) => {
  const accents = {
    default: "border-white/10",
    good: "border-[#7BAE8E]/40",
    warn: "border-[#F4B8C0]/40",
  };
  return (
    <div
      className={`bg-white/[0.04] border ${accents[accent]} rounded-2xl p-5`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
        {label}
      </div>
      <div className="text-3xl font-light text-white mb-1 tabular-nums">
        {value}
      </div>
      {hint && (
        <div className="text-xs text-white/45 leading-relaxed">{hint}</div>
      )}
    </div>
  );
};

const Panel = ({ title, meta, children, testid }) => (
  <div
    className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-7 mb-6"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
      <h2 className="text-lg font-medium text-white">{title}</h2>
      {meta && (
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          {meta}
        </span>
      )}
    </div>
    {children}
  </div>
);

// ─── Free-user bucket bar ─────────────────────────────────────────
const BucketBar = ({ label, count, total, color, testid }) => {
  const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-white/85">{label}</span>
        <span className="text-white/55 tabular-nums">
          {fmt(count)} <span className="text-white/35">· {pct}%</span>
        </span>
      </div>
      <div className="h-3 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

// ─── Conversion-funnel arrow row ─────────────────────────────────
const FunnelStep = ({ label, count, peak, dropoffPct, color, testid }) => {
  const pct = peak > 0 ? Math.round((count / peak) * 1000) / 10 : 0;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-white font-medium">{label}</span>
        <span className="text-white/55 tabular-nums">
          {fmt(count)} <span className="text-white/35">· {pct}%</span>
          {dropoffPct !== null && dropoffPct !== undefined && (
            <span className="ml-2 text-xs text-[#F4B8C0]">
              ↓ {dropoffPct.toFixed(1)}%
            </span>
          )}
        </span>
      </div>
      <div className="h-3 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

// ─── Cohort retention heatmap row ────────────────────────────────
const cohortColor = (pct) => {
  if (pct === null || pct === undefined) return "rgba(255,255,255,0.04)";
  // 0% → red-tinted ; 100% → gold-tinted
  const opacity = 0.15 + (pct / 100) * 0.65;
  if (pct >= 50) return `rgba(212, 180, 131, ${opacity})`;
  if (pct >= 20) return `rgba(244, 184, 192, ${opacity})`;
  return `rgba(155, 106, 108, ${opacity})`;
};

const CohortHeatmap = ({ cohorts }) => (
  <div className="overflow-x-auto -mx-2">
    <table className="w-full min-w-[480px] text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/45">
          <th className="px-2 py-2 font-medium">Cohort week</th>
          <th className="px-2 py-2 font-medium text-right">Size</th>
          <th className="px-2 py-2 font-medium text-center">W0</th>
          <th className="px-2 py-2 font-medium text-center">W1</th>
          <th className="px-2 py-2 font-medium text-center">W2</th>
          <th className="px-2 py-2 font-medium text-center">W3</th>
          <th className="px-2 py-2 font-medium text-center">W4</th>
        </tr>
      </thead>
      <tbody>
        {cohorts.map((c) => (
          <tr key={c.cohort_week} className="border-t border-white/[0.06]">
            <td className="px-2 py-2 text-white/80">{c.cohort_week}</td>
            <td className="px-2 py-2 text-white/60 text-right tabular-nums">
              {fmt(c.size)}
            </td>
            {[0, 1, 2, 3, 4].map((wk) => {
              const v = c[`w${wk}`];
              return (
                <td
                  key={wk}
                  className="px-2 py-2 text-center tabular-nums text-white/85 rounded"
                  style={{ background: cohortColor(v) }}
                >
                  {v === null || v === undefined ? "—" : `${v.toFixed(0)}%`}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────
export default function AdminEngagement() {
  const { loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stealth-cohort drilldown — operator-only. Cohort counts come up
  // automatically alongside the aggregate engagement load; the
  // decrypted user list is loaded on-demand so a casual admin page
  // visit doesn't write an audit row.
  const [stealthCohort, setStealthCohort] = useState(null);
  const [stealthUsers, setStealthUsers] = useState(null);
  const [stealthUsersLoading, setStealthUsersLoading] = useState(false);
  const [stealthUsersError, setStealthUsersError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchAdminEngagement();
      setData(d);
    } catch (e) {
      setError(e?.response?.status || "error");
    } finally {
      setLoading(false);
    }
    try {
      const c = await fetchAdminStealthCohort();
      setStealthCohort(c);
    } catch {
      // Non-fatal — main engagement view still renders.
    }
  };

  const loadStealthUsers = async () => {
    setStealthUsersLoading(true);
    setStealthUsersError(null);
    try {
      const res = await fetchAdminStealthUsers(200);
      setStealthUsers(res?.users || []);
    } catch (e) {
      setStealthUsersError(e?.response?.status || "error");
    } finally {
      setStealthUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  return (
    <AdminShell
      eyebrow="Engagement"
      title="Who's actually using TruBeauté"
      subtitle="Active users · free-tier engagement · conversion funnel · cohort retention · stealth-mode adoption. Aggregate-only — no per-user lists."
      actions={
        <button
          onClick={load}
          disabled={loading}
          className="text-xs rounded-full px-3 py-1.5 bg-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.12] border border-white/10 transition-colors inline-flex items-center gap-1.5"
          data-testid="engagement-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      }
    >
      {error && (
        <div className="bg-[#F4B8C0]/10 border border-[#F4B8C0]/30 rounded-2xl p-5 text-[#F4B8C0] mb-6">
          Couldn't load engagement data (status: {error}).
        </div>
      )}

      {loading && !data && (
        <div className="text-white/40 text-sm">Loading engagement data…</div>
      )}

      {data && (
        <div data-testid="admin-engagement-page">
          {/* 1 · Active users (DAU/WAU/MAU) */}
          <Panel
            title="Active users"
            meta="Distinct humans who generated a routine"
            testid="panel-active-users"
          >
            <div className="grid sm:grid-cols-3 gap-4">
              <StatTile
                label="Daily active"
                value={fmt(data.active_users.dau)}
                hint="Last 24 h · routine generated"
                testid="stat-dau"
              />
              <StatTile
                label="Weekly active"
                value={fmt(data.active_users.wau)}
                hint="Last 7 days"
                testid="stat-wau"
              />
              <StatTile
                label="Monthly active"
                value={fmt(data.active_users.mau)}
                hint="Last 30 days"
                accent="good"
                testid="stat-mau"
              />
            </div>
          </Panel>

          {/* 2 · Composition */}
          <Panel
            title="Membership composition"
            meta="Free vs Pro · all-time"
            testid="panel-composition"
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile
                label="Total members"
                value={fmt(data.composition.total_members)}
                hint="Real signups (excl. guest/stealth-only)"
                testid="stat-total-members"
              />
              <StatTile
                label="Free tier"
                value={fmt(data.composition.free)}
                hint="No active Pro"
                testid="stat-free"
              />
              <StatTile
                label="Pro"
                value={fmt(data.composition.pro)}
                hint={`${fmtPct(data.composition.pro_share_pct)} of total`}
                accent="good"
                testid="stat-pro"
              />
              <StatTile
                label="Founding members"
                value={fmt(
                  data.composition.founding_members +
                  data.composition.founding_lock_in
                )}
                hint={
                  <>
                    {fmt(data.composition.founding_members)} beta ·{" "}
                    {fmt(data.composition.founding_lock_in)} lock-in
                  </>
                }
                testid="stat-founding"
              />
            </div>
            {Object.keys(data.composition.pro_by_source || {}).length > 0 && (
              <div className="mt-5 pt-5 border-t border-white/[0.08]">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">
                  Pro by source
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.composition.pro_by_source).map(
                    ([src, n]) => (
                      <span
                        key={src}
                        className="text-xs bg-white/[0.06] border border-white/10 rounded-full px-3 py-1 text-white/70"
                        data-testid={`pro-source-${src}`}
                      >
                        {src}: <span className="text-white tabular-nums">{fmt(n)}</span>
                      </span>
                    )
                  )}
                </div>
              </div>
            )}
          </Panel>

          {/* 3 · Free-user engagement */}
          <Panel
            title="Free-tier engagement"
            meta="Last 30 days · per-user routine count"
            testid="panel-free-engagement"
          >
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <BucketBar
                  label="0 routines (signed up, never returned)"
                  count={data.free_user_engagement.buckets["0_routines"]}
                  total={data.free_user_engagement.free_total}
                  color="#F4B8C0"
                  testid="free-bucket-0"
                />
                <BucketBar
                  label="1 routine"
                  count={data.free_user_engagement.buckets["1_routine"]}
                  total={data.free_user_engagement.free_total}
                  color="#D4B483"
                  testid="free-bucket-1"
                />
                <BucketBar
                  label="2 routines"
                  count={data.free_user_engagement.buckets["2_routines"]}
                  total={data.free_user_engagement.free_total}
                  color="#C4A373"
                  testid="free-bucket-2"
                />
                <BucketBar
                  label="3 routines — paywall hit"
                  count={data.free_user_engagement.buckets["3_routines_capped"]}
                  total={data.free_user_engagement.free_total}
                  color="#7BAE8E"
                  testid="free-bucket-3"
                />
              </div>
              <div className="space-y-4">
                <StatTile
                  label="30-day active free users"
                  value={fmt(data.free_user_engagement.free_30d_active)}
                  hint={`of ${fmt(data.free_user_engagement.free_total)} total free`}
                  testid="stat-free-30d-active"
                />
                <StatTile
                  label="Avg routines per active free user"
                  value={
                    data.free_user_engagement.avg_routines_per_active_free.toFixed(
                      2
                    )
                  }
                  hint="Engagement quality signal"
                  testid="stat-free-avg-routines"
                />
              </div>
            </div>
          </Panel>

          {/* 4 · Conversion funnel */}
          <Panel
            title="Free → Pro conversion funnel"
            meta="All-time"
            testid="panel-conversion-funnel"
          >
            <div className="space-y-4 mb-6">
              {[
                {
                  key: "signed_up",
                  label: "Signed up",
                  color: "#D4B483",
                  dropoff: null,
                },
                {
                  key: "did_first_routine",
                  label: "Did first routine",
                  color: "#C4A373",
                  dropoff:
                    100 -
                    data.conversion_funnel.conversion_pct.signup_to_first_routine,
                },
                {
                  key: "hit_paywall",
                  label: "Hit paywall (3+ routines in a month)",
                  color: "#9B6A6C",
                  dropoff:
                    100 -
                    data.conversion_funnel.conversion_pct
                      .first_routine_to_paywall,
                },
                {
                  key: "currently_pro",
                  label: "Currently Pro",
                  color: "#7BAE8E",
                  dropoff:
                    100 -
                    data.conversion_funnel.conversion_pct.paywall_to_pro,
                },
              ].map((step) => (
                <FunnelStep
                  key={step.key}
                  label={step.label}
                  count={data.conversion_funnel[step.key]}
                  peak={data.conversion_funnel.signed_up}
                  color={step.color}
                  dropoffPct={step.dropoff}
                  testid={`funnel-${step.key}`}
                />
              ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-5 border-t border-white/[0.08]">
              <StatTile
                label="Signup → first routine"
                value={fmtPct(
                  data.conversion_funnel.conversion_pct.signup_to_first_routine
                )}
                hint="Activation rate"
                testid="rate-signup-routine"
              />
              <StatTile
                label="First routine → paywall"
                value={fmtPct(
                  data.conversion_funnel.conversion_pct
                    .first_routine_to_paywall
                )}
                hint="Engaged-enough-to-hit-cap"
                testid="rate-routine-paywall"
              />
              <StatTile
                label="Paywall → Pro"
                value={fmtPct(
                  data.conversion_funnel.conversion_pct.paywall_to_pro
                )}
                hint="Paywall conversion"
                accent="good"
                testid="rate-paywall-pro"
              />
              <StatTile
                label="Signup → Pro"
                value={fmtPct(
                  data.conversion_funnel.conversion_pct.signup_to_pro
                )}
                hint="End-to-end"
                testid="rate-signup-pro"
              />
            </div>
          </Panel>

          {/* 5 · Cohort retention */}
          <Panel
            title="Weekly cohort retention"
            meta={`Last ${data.retention_cohorts.weeks_back} weeks`}
            testid="panel-cohort-retention"
          >
            <CohortHeatmap cohorts={data.retention_cohorts.cohorts} />
            <p className="text-xs text-white/40 mt-4 leading-relaxed">
              Each row is a sign-up cohort. Cells show % of that cohort who
              generated a routine that week. <span className="text-white/60">W0</span> is
              the sign-up week (always &gt;0 by definition for engaged cohorts);
              <span className="text-white/60"> W4</span> retention is the number
              investors will ask about.
            </p>
          </Panel>

          {/* 6 · Stealth mode */}
          <Panel
            title="Stealth mode usage"
            meta="Privacy promise — is it actually used?"
            testid="panel-stealth-mode"
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              <StatTile
                label="Routines (30d)"
                value={fmt(data.stealth_mode.routines_30d)}
                hint="Including stealth"
                testid="stat-routines-30d"
              />
              <StatTile
                label="Stealth routines"
                value={fmt(data.stealth_mode.stealth_routines_30d)}
                hint={`${fmtPct(data.stealth_mode.stealth_routine_share_pct)} of all routines`}
                accent="good"
                testid="stat-stealth-routines"
              />
              <StatTile
                label="Users · stealth default ON"
                value={fmt(data.stealth_mode.users_with_stealth_default_on)}
                hint={`${fmtPct(data.stealth_mode.stealth_default_share_pct)} of members`}
                testid="stat-stealth-default-on"
              />
              <StatTile
                label="Saved (non-stealth)"
                value={fmt(data.stealth_mode.saved_routines_30d)}
                hint="Stored encrypted for /my-routines"
                testid="stat-saved-routines"
              />
            </div>
            <p className="text-xs text-white/40 leading-relaxed">
              <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />
              Stealth-mode routines are generated &amp; delivered but{" "}
              <span className="text-white/65">never persisted</span> — investors
              can read this share as a privacy-feature adoption signal.
            </p>
          </Panel>

          {/* 7 · Stealth users — operator-only drilldown.
              Cohort summary + on-demand decrypted-email list.
              The list endpoint writes an admin_audit_log row on every
              call, so we don't pre-load it on page boot. */}
          <Panel
            title="Stealth users — operator only"
            meta="Audit-logged · not for investor view"
            testid="panel-stealth-users"
          >
            {stealthCohort ? (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  <StatTile
                    label="Unique stealth users"
                    value={fmt(stealthCohort.unique_stealth_users)}
                    hint={`${fmt(stealthCohort.total_stealth_routines_lifetime)} stealth routines lifetime`}
                    accent="good"
                    testid="stat-stealth-unique"
                  />
                  <StatTile
                    label="Multi-routine"
                    value={fmt(stealthCohort.multi_routine_stealth_users)}
                    hint="≥ 2 stealth routines"
                    testid="stat-stealth-multi-routine"
                  />
                  <StatTile
                    label="Multi-month"
                    value={fmt(stealthCohort.multi_month_stealth_users)}
                    hint="Stealth across ≥ 2 distinct months"
                    testid="stat-stealth-multi-month"
                  />
                  <StatTile
                    label="Returning"
                    value={fmt(stealthCohort.returning_stealth_users)}
                    hint="Any activity in ≥ 2 distinct months"
                    testid="stat-stealth-returning"
                  />
                </div>

                <div className="text-xs text-white/45 leading-relaxed mb-4 flex flex-wrap gap-x-4 gap-y-1">
                  <span>
                    Avg per user:{" "}
                    <span className="text-white/80 tabular-nums">
                      {stealthCohort.avg_stealth_routines_per_user}
                    </span>
                  </span>
                  <span>
                    Median:{" "}
                    <span className="text-white/80 tabular-nums">
                      {stealthCohort.median_stealth_routines_per_user}
                    </span>
                  </span>
                  <span>
                    Max (one user):{" "}
                    <span className="text-white/80 tabular-nums">
                      {stealthCohort.max_stealth_routines_one_user}
                    </span>
                  </span>
                </div>
              </>
            ) : (
              <div className="text-white/40 text-xs mb-5">
                Loading stealth cohort…
              </div>
            )}

            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <p className="text-xs text-white/55 leading-relaxed flex-1 min-w-[260px]">
                <EyeOff className="inline h-3 w-3 mr-1 -mt-0.5" />
                Decrypted-email list of every real user with at least one
                stealth routine. Every load writes a{" "}
                <code className="bg-white/10 px-1 py-0.5 rounded text-[#D4B483]">
                  stealth_user_cohort_listed
                </code>{" "}
                row to the admin audit log.
              </p>
              <button
                onClick={loadStealthUsers}
                disabled={stealthUsersLoading}
                className="text-xs rounded-full px-3 py-1.5 bg-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.12] border border-white/10 transition-colors inline-flex items-center gap-1.5"
                data-testid="stealth-users-load-btn"
              >
                <RefreshCw
                  className={`h-3 w-3 ${stealthUsersLoading ? "animate-spin" : ""}`}
                />
                {stealthUsers ? "Reload list" : "Load user list"}
              </button>
            </div>

            {stealthUsersError && (
              <div className="bg-[#F4B8C0]/10 border border-[#F4B8C0]/30 rounded-xl p-3 text-[#F4B8C0] text-xs mb-4">
                Couldn't load stealth users (status: {stealthUsersError}).
              </div>
            )}

            {stealthUsers && stealthUsers.length === 0 && (
              <div className="text-white/40 text-xs italic">
                No stealth users yet.
              </div>
            )}

            {stealthUsers && stealthUsers.length > 0 && (
              <div
                className="overflow-x-auto -mx-2"
                data-testid="stealth-users-table-wrap"
              >
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-white/45 uppercase tracking-[0.18em] text-[10px]">
                      <th className="px-2 py-2 text-left">Email</th>
                      <th className="px-2 py-2 text-left">Signed up</th>
                      <th className="px-2 py-2 text-left">Last active</th>
                      <th className="px-2 py-2 text-right">Stealth</th>
                      <th className="px-2 py-2 text-right">Saved</th>
                      <th className="px-2 py-2 text-right">Months</th>
                      <th className="px-2 py-2 text-center">Returning</th>
                      <th className="px-2 py-2 text-center">Default on</th>
                      <th className="px-2 py-2 text-center">Pro</th>
                    </tr>
                  </thead>
                  <tbody data-testid="stealth-users-table-body">
                    {stealthUsers.map((u) => (
                      <tr
                        key={u.user_id}
                        className="border-t border-white/5 text-white/85"
                        data-testid={`stealth-user-row-${u.user_id}`}
                      >
                        <td
                          className="px-2 py-2 text-white/85 truncate max-w-[220px]"
                          title={u.email || u.user_id}
                        >
                          {u.email || (
                            <span className="text-white/30">
                              (decrypt failed)
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-white/60 tabular-nums">
                          {u.signed_up_at
                            ? u.signed_up_at.slice(0, 10)
                            : "—"}
                        </td>
                        <td className="px-2 py-2 text-white/60 tabular-nums">
                          {u.last_activity_at
                            ? u.last_activity_at.slice(0, 10)
                            : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-[#7BAE8E]">
                          {u.stealth_routines}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-white/70">
                          {u.non_stealth_routines}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-white/70">
                          {u.distinct_stealth_months}
                        </td>
                        <td className="px-2 py-2 text-center text-white/60">
                          {u.is_returning ? "✓" : "—"}
                        </td>
                        <td className="px-2 py-2 text-center text-white/60">
                          {u.stealth_default_on ? "✓" : "—"}
                        </td>
                        <td className="px-2 py-2 text-center text-white/60">
                          {u.is_pro ? "✓" : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <div className="text-[10px] uppercase tracking-[0.22em] text-white/30 mt-2 text-right">
            Generated: {data.generated_at?.slice(0, 19).replace("T", " ")} UTC
          </div>
        </div>
      )}
    </AdminShell>
  );
}
